// Plans the Leads master-list reconciliation.
//
// Leads is the master working list: every unique prospect in Hot, Audit, Leads
// or Old Leads must exist exactly once in Leads. Source rows are never moved or
// modified — a prospect stays on its own tab and simply gains a Leads twin.

import { identityKeysFor } from './prospecting-identity.mjs'

export const RECONCILE_SOURCE_LISTS = Object.freeze(['hot', 'audit', 'old'])

export const PROSPECTING_LIST_LABELS = Object.freeze({
  hot: 'Hot',
  audit: 'Audit',
  leads: 'Leads',
  old: 'Old Leads',
})

/**
 * @param {{ hot?: object[], audit?: object[], leads?: object[], old?: object[] }} lists
 *   Parsed records per tab, as produced by parseProspectingRows.
 * @returns {{ appends: object[], rows: string[][], skipped: number, scanned: number }}
 *   `rows` are the raw A:K cells to append to Leads, in source order.
 */
export function planLeadsReconciliation(lists) {
  // Every identifier already represented in Leads. Membership is tested against
  // this whole set, so an Old Leads row that only carries a website still
  // matches a Leads row that carries the same website under a different name.
  const known = new Set()
  for (const record of lists.leads ?? []) {
    for (const key of identityKeysFor(record)) known.add(key)
  }

  const appends = []
  let skipped = 0
  let scanned = 0

  for (const list of RECONCILE_SOURCE_LISTS) {
    for (const record of lists[list] ?? []) {
      const keys = identityKeysFor(record)
      scanned += 1

      // A record with no usable identifier at all is left alone rather than
      // appended blindly, because it could never be de-duplicated afterwards.
      if (!keys.length) {
        skipped += 1
        continue
      }

      if (keys.some((key) => known.has(key))) {
        // Learn the identifiers this row contributes to an identity that is
        // already in Leads. That keeps a later row matching on a weaker signal
        // from being appended as a duplicate.
        for (const key of keys) known.add(key)
        skipped += 1
        continue
      }

      for (const key of keys) known.add(key)
      appends.push({ list, rowNumber: record.rowNumber, person: record.person, keys, raw: record.raw })
    }
  }

  return { appends, rows: appends.map((append) => append.raw), skipped, scanned }
}
